import { useEffect, useState } from 'react'
import {
  Search, ChevronRight, ArrowLeft, Pencil, Trash2, ExternalLink, MapPin, X, Building2, Users, Mail, Phone,
} from 'lucide-react'
import MobileLayout from './MobileLayout'
import { apiService, Company, Contact, CompanyCustomField, CompanyCustomFieldOption } from '@/services/api'
import { useToast } from '@/hooks/use-toast'
import { compareByLastName } from '@/utils/name-format'

type CustomFieldDef = CompanyCustomField & { options?: CompanyCustomFieldOption[] }
type CompanyDetail = Company & {
  custom_fields?: { field: { id: number; name: string }; value?: string }[]
}

export default function MobileCompanies() {
  const { toast } = useToast()
  const [companies, setCompanies] = useState<Company[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')

  const [detail, setDetail] = useState<CompanyDetail | null>(null)
  const [detailContacts, setDetailContacts] = useState<Contact[]>([])
  const [loadingDetailContacts, setLoadingDetailContacts] = useState(false)
  const [contactToRemove, setContactToRemove] = useState<Contact | null>(null)
  const [formOpen, setFormOpen] = useState(false)
  const [editId, setEditId] = useState<number | null>(null)
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState({ name: '', website: '', street_address: '', address_complement: '', postal_code: '', city: '' })
  const [customFields, setCustomFields] = useState<CustomFieldDef[]>([])
  const [customValues, setCustomValues] = useState<Record<number, string>>({})
  const [confirmDelete, setConfirmDelete] = useState<CompanyDetail | null>(null)

  const loadCompanies = async () => {
    setLoading(true)
    try {
      const res = await apiService.getCompanies({ page: 1, limit: 100, search: search || undefined, sort: 'name', order: 'asc' })
      if (res.success && res.data) setCompanies(res.data.data)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    const t = setTimeout(loadCompanies, 300)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search])

  const loadDetailContacts = async (id: number) => {
    setLoadingDetailContacts(true)
    try {
      const cres = await apiService.getContacts({
        page: 1,
        limit: 50,
        filters: { filters: [{ field: 'company_id', operator: 'equals', value: id.toString() }], logicOperator: 'AND' } as any,
      })
      if (cres.success && cres.data?.data) {
        // Même présentation que sur le poste de travail : par ordre alphabétique de nom
        setDetailContacts(
          cres.data.data
            .filter((c) => String(c.company_id) === String(id))
            .sort(compareByLastName),
        )
      } else {
        setDetailContacts([])
      }
    } finally {
      setLoadingDetailContacts(false)
    }
  }

  const openDetail = async (id: number) => {
    const res = await apiService.getCompany(id)
    if (res.success && res.data) setDetail(res.data as CompanyDetail)
    setDetailContacts([])
    loadDetailContacts(id)
  }

  const removeContact = async () => {
    if (!contactToRemove || !detail) return
    const res = await apiService.deleteContact(contactToRemove.id)
    if (res.success) {
      toast({ title: 'Contact mis à la corbeille', variant: 'success' })
      setContactToRemove(null)
      loadDetailContacts(detail.id)
    } else {
      toast({ variant: 'destructive', title: 'Erreur', description: 'Suppression impossible.' })
    }
  }

  const openCreate = async () => {
    setEditId(null)
    setForm({ name: '', website: '', street_address: '', address_complement: '', postal_code: '', city: '' })
    setCustomValues({})
    setFormOpen(true)
    const cf = await apiService.getCompanyCustomFieldsWithUsage()
    if (cf.success && cf.data) setCustomFields(cf.data)
  }

  const openEdit = async (c: CompanyDetail) => {
    setEditId(c.id)
    setForm({
      name: c.name, website: c.website || '', street_address: c.street_address || '',
      address_complement: c.address_complement || '', postal_code: c.postal_code || '', city: c.city || '',
    })
    const vals: Record<number, string> = {}
    c.custom_fields?.forEach((cf) => { if (cf.value) vals[cf.field.id] = cf.value })
    setCustomValues(vals)
    setDetail(null)
    setFormOpen(true)
    const cf = await apiService.getCompanyCustomFieldsWithUsage()
    if (cf.success && cf.data) setCustomFields(cf.data)
  }

  const save = async () => {
    if (!form.name.trim()) {
      toast({ variant: 'destructive', title: 'Champ requis', description: 'Le nom de l\'entreprise est obligatoire.' })
      return
    }
    setSaving(true)
    try {
      const payload: any = {
        name: form.name, website: form.website || undefined,
        street_address: form.street_address || undefined, address_complement: form.address_complement || undefined,
        postal_code: form.postal_code || undefined, city: form.city || undefined,
        custom_fields: Object.entries(customValues)
          .filter(([, v]) => v !== '' && v != null)
          .map(([k, v]) => ({ company_custom_field_id: parseInt(k, 10), value: v })),
      }
      const res = editId ? await apiService.updateCompany(editId, payload) : await apiService.createCompany(payload)
      if (res.success) {
        toast({ title: editId ? 'Entreprise modifiée' : 'Entreprise créée', variant: 'success' })
        setFormOpen(false)
        loadCompanies()
      } else {
        toast({ variant: 'destructive', title: 'Erreur', description: res.error || 'Échec de l\'enregistrement.' })
      }
    } finally {
      setSaving(false)
    }
  }

  const doDelete = async () => {
    if (!confirmDelete) return
    const res = await apiService.deleteCompany(confirmDelete.id)
    if (res.success) {
      toast({ title: 'Entreprise mise à la corbeille', variant: 'success' })
      setConfirmDelete(null)
      setDetail(null)
      loadCompanies()
    } else {
      toast({ variant: 'destructive', title: 'Erreur', description: 'Suppression impossible.' })
    }
  }

  return (
    <MobileLayout title="Entreprises" onAdd={openCreate}>
      <div className="px-4 py-3 sticky top-0 bg-white z-10 border-b border-gray-50">
        <div className="relative">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full rounded-xl bg-gray-100 border-0 pl-9 pr-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
            placeholder="Rechercher une entreprise…"
          />
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-7 w-7 border-b-2 border-blue-600" /></div>
      ) : companies.length === 0 ? (
        <p className="text-center text-sm text-gray-400 py-16">Aucune entreprise</p>
      ) : (
        <div className="divide-y divide-gray-100">
          {companies.map((c) => (
            <button key={c.id} onClick={() => openDetail(c.id)} className="w-full flex items-center gap-3 px-4 py-3 active:bg-gray-50 text-left">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 text-emerald-600 flex items-center justify-center font-bold shrink-0">
                {c.name.charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-gray-900 truncate">{c.name}</p>
                <p className="text-xs text-gray-500 truncate">{[c.postal_code, c.city].filter(Boolean).join(' ') || 'Adresse non renseignée'}</p>
              </div>
              <ChevronRight className="w-4 h-4 text-gray-300 shrink-0" />
            </button>
          ))}
        </div>
      )}

      {/* Détail */}
      {detail && (
        <div className="fixed inset-0 z-30 bg-white flex flex-col">
          <div className="flex items-center gap-2 px-3 py-3 border-b border-gray-100 shrink-0">
            <button onClick={() => setDetail(null)} className="p-1.5 text-gray-500"><ArrowLeft className="w-5 h-5" /></button>
            <span className="font-semibold text-gray-700 text-sm">Détail</span>
            <div className="ml-auto flex gap-1">
              <button onClick={() => openEdit(detail)} className="p-1.5 text-gray-500"><Pencil className="w-5 h-5" /></button>
              <button onClick={() => setConfirmDelete(detail)} className="p-1.5 text-rose-500"><Trash2 className="w-5 h-5" /></button>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            <div className="bg-gradient-to-br from-blue-600 to-indigo-700 px-5 pt-6 pb-8 text-center text-white">
              <div className="w-20 h-20 rounded-2xl bg-white/15 ring-2 ring-white/30 mx-auto flex items-center justify-center">
                <Building2 className="w-9 h-9" />
              </div>
              <h2 className="text-xl font-bold mt-3">{detail.name}</h2>
            </div>
            <div className="px-4 py-4 space-y-3">
              <div className="rounded-2xl border border-blue-100 bg-blue-50/50 overflow-hidden">
                <div className="px-3 py-2 bg-blue-100/70 text-xs font-bold text-blue-800 uppercase tracking-wide">Informations</div>
                {detail.website && (
                  <a href={detail.website.startsWith('http') ? detail.website : `https://${detail.website}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-3 py-2.5 text-blue-600">
                    <ExternalLink className="w-4 h-4 shrink-0" /><span className="font-semibold truncate">{detail.website}</span>
                  </a>
                )}
                {(detail.street_address || detail.city || detail.postal_code) && (
                  <div className="flex items-start gap-2 px-3 py-2.5 border-t border-blue-100">
                    <MapPin className="w-4 h-4 text-blue-500 shrink-0 mt-0.5" />
                    <div className="font-semibold text-gray-800 text-sm">
                      {detail.street_address && <p>{detail.street_address}</p>}
                      {detail.address_complement && <p>{detail.address_complement}</p>}
                      <p>{[detail.postal_code, detail.city].filter(Boolean).join(' ')}</p>
                    </div>
                  </div>
                )}
              </div>
              {detail.custom_fields && detail.custom_fields.filter((c) => c.value).length > 0 && (
                <div className="rounded-2xl border border-amber-100 bg-amber-50/50 overflow-hidden">
                  <div className="px-3 py-2 bg-amber-100/70 text-xs font-bold text-amber-800 uppercase tracking-wide">Champs personnalisés</div>
                  <div className="divide-y divide-amber-100">
                    {detail.custom_fields.filter((c) => c.value).map((cf) => (
                      <div key={cf.field.id} className="px-3 py-2.5"><span className="text-xs text-gray-400">{cf.field.name}</span><p className="font-semibold text-gray-800">{cf.field.name.trim().toLowerCase() === 'typologie' ? (cf.value || '').toUpperCase() : cf.value}</p></div>
                    ))}
                  </div>
                </div>
              )}

              {/* Contacts associés */}
              <div className="rounded-2xl border border-emerald-100 bg-emerald-50/50 overflow-hidden">
                <div className="flex items-center gap-1.5 px-3 py-2 bg-emerald-100/70 text-xs font-bold text-emerald-800 uppercase tracking-wide">
                  <Users className="w-3.5 h-3.5" /> Contacts ({detailContacts.length})
                </div>
                {loadingDetailContacts ? (
                  <div className="flex justify-center py-5"><div className="animate-spin rounded-full h-5 w-5 border-b-2 border-emerald-600" /></div>
                ) : detailContacts.length > 0 ? (
                  <div className="divide-y divide-emerald-100">
                    {detailContacts.map((ct) => (
                      <div key={ct.id} className="flex items-center gap-3 px-3 py-2.5">
                        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-xs font-bold shrink-0">
                          {`${ct.first_name?.[0] ?? ''}${ct.last_name?.[0] ?? ''}`.toUpperCase() || '?'}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="font-semibold text-gray-800 truncate">{ct.last_name} {ct.first_name}</p>
                          {ct.email && <p className="text-xs text-gray-500 truncate">{ct.email}</p>}
                        </div>
                        <div className="flex gap-1 shrink-0">
                          {ct.phone && <a href={`tel:${ct.phone}`} className="p-1.5 rounded-lg bg-blue-50 text-blue-600"><Phone className="w-4 h-4" /></a>}
                          {ct.email && <a href={`mailto:${ct.email}`} className="p-1.5 rounded-lg bg-emerald-50 text-emerald-600"><Mail className="w-4 h-4" /></a>}
                          <button onClick={() => setContactToRemove(ct)} className="p-1.5 rounded-lg bg-rose-50 text-rose-600"><Trash2 className="w-4 h-4" /></button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-400 text-center py-5">Aucun contact associé</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Formulaire */}
      {formOpen && (
        <div className="fixed inset-0 z-40 bg-white flex flex-col">
          <div className="flex items-center gap-2 px-3 py-3 border-b border-gray-100 shrink-0">
            <button onClick={() => setFormOpen(false)} className="p-1.5 text-gray-500"><X className="w-5 h-5" /></button>
            <span className="font-semibold text-gray-800">{editId ? 'Modifier l\'entreprise' : 'Nouvelle entreprise'}</span>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            <Field label="Nom de l'entreprise *"><input className="inp" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nom" /></Field>
            <Field label="Site web"><input className="inp" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="https://exemple.com" /></Field>
            <Field label="Adresse"><input className="inp" value={form.street_address} onChange={(e) => setForm({ ...form, street_address: e.target.value })} placeholder="Rue et numéro" /></Field>
            <Field label="Complément d'adresse"><input className="inp" value={form.address_complement} onChange={(e) => setForm({ ...form, address_complement: e.target.value })} placeholder="Bâtiment, étage…" /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Code postal"><input className="inp" value={form.postal_code} onChange={(e) => setForm({ ...form, postal_code: e.target.value })} placeholder="Code postal" /></Field>
              <Field label="Ville"><input className="inp" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="Ville" /></Field>
            </div>

            {customFields.length > 0 && (
              <div className="pt-2">
                <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Champs personnalisés</p>
                <div className="space-y-3">
                  {customFields.map((cf) => (
                    <Field key={cf.id} label={cf.name}>
                      <CustomInput field={cf} value={customValues[cf.id] || ''} onChange={(v) => setCustomValues({ ...customValues, [cf.id]: v })} />
                    </Field>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="border-t border-gray-100 p-3 shrink-0">
            <button onClick={save} disabled={saving} className="w-full py-3 rounded-xl bg-blue-600 text-white font-semibold disabled:opacity-60">
              {saving ? 'Enregistrement…' : editId ? 'Enregistrer' : 'Créer l\'entreprise'}
            </button>
          </div>
        </div>
      )}

      {confirmDelete && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center" onClick={() => setConfirmDelete(null)}>
          <div className="bg-white w-full sm:max-w-sm rounded-t-2xl sm:rounded-2xl p-5" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-bold text-gray-900">Supprimer cette entreprise ?</h3>
            <p className="text-sm text-gray-500 mt-1">{confirmDelete.name} : la fiche ira dans la corbeille, d'où un administrateur pourra la restaurer.</p>
            <div className="flex gap-3 mt-5">
              <button onClick={() => setConfirmDelete(null)} className="flex-1 py-2.5 rounded-xl border border-gray-200 font-semibold text-gray-700">Annuler</button>
              <button onClick={doDelete} className="flex-1 py-2.5 rounded-xl bg-rose-600 text-white font-semibold">Supprimer</button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation suppression d'un contact associé */}
      {contactToRemove && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center" onClick={() => setContactToRemove(null)}>
          <div className="bg-white w-full sm:max-w-sm rounded-t-2xl sm:rounded-2xl p-5" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-bold text-gray-900">Supprimer ce contact ?</h3>
            <p className="text-sm text-gray-500 mt-1">{contactToRemove.first_name} {contactToRemove.last_name} sera définitivement supprimé du CRM.</p>
            <div className="flex gap-3 mt-5">
              <button onClick={() => setContactToRemove(null)} className="flex-1 py-2.5 rounded-xl border border-gray-200 font-semibold text-gray-700">Annuler</button>
              <button onClick={removeContact} className="flex-1 py-2.5 rounded-xl bg-rose-600 text-white font-semibold">Supprimer</button>
            </div>
          </div>
        </div>
      )}

      <style>{`.inp{width:100%;border:1px solid #e5e7eb;border-radius:0.5rem;padding:0.5rem 0.75rem;font-size:0.95rem}.inp:focus{outline:2px solid #3b82f6;border-color:transparent}`}</style>
    </MobileLayout>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-sm font-medium text-gray-700">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  )
}

function CustomInput({ field, value, onChange }: { field: CustomFieldDef; value: string; onChange: (v: string) => void }) {
  if (field.type === 'radio') {
    return (
      <div className="inline-flex p-0.5 rounded-lg bg-gray-100 text-sm font-medium">
        {field.options?.map((o) => {
          const ov = o.value || o.label
          const sel = value === ov
          const lower = o.label.trim().toLowerCase()
          const cls = lower === 'oui' ? 'bg-emerald-600 text-white' : lower === 'non' ? 'bg-rose-500 text-white' : 'bg-blue-600 text-white'
          return <button key={o.id} type="button" onClick={() => onChange(ov)} className={`px-3 py-1 rounded-md ${sel ? cls : 'text-gray-500'}`}>{o.label}</button>
        })}
      </div>
    )
  }
  if (field.type === 'select') {
    return (
      <select className="inp" value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">Sélectionner…</option>
        {field.options?.map((o) => <option key={o.id} value={o.value || o.label}>{o.label}</option>)}
      </select>
    )
  }
  const type = field.type === 'number' ? 'number' : field.type === 'email' ? 'email' : field.type === 'date' ? 'date' : field.type === 'url' ? 'url' : 'text'
  return <input className="inp" type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={`Entrez ${field.name.toLowerCase()}`} />
}
